import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { Button, Row, Col, Typography, message } from 'antd';

import { useSearch } from 'hooks/search';

import * as Yup from 'yup';
import { translate } from 'utils/booleanQuery';
import { booleanQueryErrorMessageId } from 'utils/booleanQueryErrorMessage';
import countOperators from 'utils/countOperators';
import Input from './Input';
import MultipleSelect from './MultipleSelect';
import RangeDatePicker from './RangeDatePicker';

import Select from './Select';
import { Form, Card } from './styles';

const SearchForm: React.FC = () => {
  const intl = useIntl();
  const history = useHistory();
  const {
    params,
    preview,
    booleanMode,
    setParams,
    handleObligatoryChange,
  } = useSearch();
  const [isValidQuery, setIsValidQuery] = useState(false);

  const handleValidation = useCallback(
    (field: 'obligatory' | 'optional' | 'banned', value: string) => {
      const { obligatory, optional, banned } = params;

      const updatedParams = {
        obligatory,
        optional,
        banned,
        [field]: value,
      };

      const result = translate(updatedParams);

      if (result.error) {
        message.error(
          intl.formatMessage({
            id: booleanQueryErrorMessageId(result.error.code),
          }),
        );
        setIsValidQuery(false);
        return;
      }

      const formValidationFormat = {
        terms: result.query,
        operators: countOperators(result.query),
      };

      const schema = Yup.object().shape({
        terms: Yup.string().max(
          2048,
          intl.formatMessage({ id: 'searchform.error.maxsize' }),
        ),
        operators: Yup.number().max(
          100,
          intl.formatMessage({ id: 'searchform.error.maxoperators' }),
        ),
      });

      schema
        .validate(formValidationFormat)
        .then(() => {
          setIsValidQuery(true);
        })
        .catch((err: Yup.ValidationError) => {
          message.error(err.message);
          setIsValidQuery(false);
        });
    },
    [intl, params],
  );

  const handleSubmit = useCallback(async () => {
    try {
      const { date, obligatory, optional, banned } = params;

      const result = translate({ obligatory, optional, banned });

      if (result.error) {
        message.error(
          intl.formatMessage({
            id: booleanQueryErrorMessageId(result.error.code),
          }),
        );
        return;
      }

      const formValidationFormat = {
        terms: result.query,
      };

      const schema = Yup.object().shape({
        terms: Yup.string().max(
          2048,
          intl.formatMessage({ id: 'searchform.error.maxsize' }),
        ),
      });

      await schema.validate(formValidationFormat, {
        abortEarly: false,
      });

      const urlParams = new URLSearchParams({
        obligatory,
        optional,
        banned,
        languages: JSON.stringify(params.languages),
        dateType: String(date.type),
        dateRange: JSON.stringify([date.start, date.end]),
      }).toString();
      history.push(`/results?${urlParams}`);
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        message.error(error.message);
      }
    }
  }, [history, intl, params]);

  return (
    <Card>
      <Form onFinish={handleSubmit}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={16}>
            <Input
              label={intl.formatMessage({ id: 'searchform.allwords' })}
              value={params.obligatory}
              onChange={e => handleObligatoryChange(e.target.value)}
              onBlur={e => handleValidation('obligatory', e.target.value)}
              maxLength={2048 - (params.optional.length + params.banned.length)}
              booleanMode={booleanMode}
            />
            <Input
              label={intl.formatMessage({ id: 'searchform.somewords' })}
              value={params.optional}
              onChange={e => setParams({ ...params, optional: e.target.value })}
              onBlur={e => handleValidation('optional', e.target.value)}
              maxLength={
                2048 - (params.obligatory.length + params.banned.length)
              }
            />
            <Input
              label={intl.formatMessage({ id: 'searchform.nonewords' })}
              value={params.banned}
              onChange={e => setParams({ ...params, banned: e.target.value })}
              onBlur={e => handleValidation('banned', e.target.value)}
              maxLength={
                2048 - (params.optional.length + params.obligatory.length)
              }
            />
          </Col>
          <Col xs={24} md={8}>
            <MultipleSelect
              label={intl.formatMessage({ id: 'searchform.language' })}
            />
            <Select label={intl.formatMessage({ id: 'searchform.period' })} />
            {params.date.type === 'personalized' && (
              <RangeDatePicker
                label={intl.formatMessage({ id: 'searchform.date-range' })}
              />
            )}
          </Col>
          <Col
            xs={24}
            md={16}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Typography.Paragraph
              ellipsis={{ rows: 1, expandable: true }}
              style={{ minHeight: '22px', width: '100%', margin: '0' }}
            >
              {preview}
            </Typography.Paragraph>
          </Col>
          <Col xs={24} md={8}>
            <Button
              block
              type="primary"
              htmlType="submit"
              disabled={
                (!params.obligatory && !params.optional && !params.banned) ||
                !isValidQuery
              }
            >
              {intl.formatMessage({ id: 'searchform.button' })}
            </Button>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default SearchForm;
